import { intl } from 'lang/intl'
import Video, {
  LocalParticipant,
  LocalTrack,
  NetworkQualityMediaStats,
  Participant,
  RemoteParticipant,
  RemoteTrack,
  Room,
  StatsReport,
  VideoTrack,
} from 'twilio-video'
import { setFatalError } from '../../../FatalError/FatalError'
import { logMessage } from '../../../Logging/sentry'

import {
  addParticipant,
  addTrack,
  disconnect,
  LocalTrackKey,
  removeParticipant,
  removeTrack,
  TrackKey,
  TrackPriority,
  updateParticipantQuality,
  updateStatus,
  updateTrackState,
  VideoTrackKey,
} from '../../webRTCRoom'

import {
  extractIdFromIdentity,
  extractInfoFromIdentity,
  getNormalizedTrackName,
  translateNetworkQualityLevel,
} from './twilioHelper'
import { getIsClientSecondCam } from '../../../global/roomInfo/roomInfo'
import { initLogging } from './twilioLogger'

const RENDER_DIMENSIONS: Record<TrackPriority, VideoTrack.Dimensions> = {
  low: { width: 144, height: 98 },
  standard: { width: 640, height: 360 },
  high: { width: 1920, height: 1080 },
}

let twilioRoom: Room | undefined

const connect = async (token: string) => {
  updateStatus('connecting')
  initLogging()

  // @see https://www.twilio.com/docs/video/tutorials/developing-high-quality-video-applications
  const newTwilioRoom = await Video.connect(token, {
    automaticSubscription: true,
    preferredVideoCodecs: [
      { codec: 'VP8', simulcast: true },
      { codec: 'H264' },
    ],
    networkQuality: {
      local: 3,
      remote: 2,
    },
    bandwidthProfile: {
      video: {
        mode: 'presentation',
        trackSwitchOffMode: 'disabled',
        clientTrackSwitchOffControl: 'manual',
        contentPreferencesMode: 'manual',
      },
    },
    tracks: [],
    enableDscp: true,
  })

  if (!newTwilioRoom) {
    setFatalError(
      intl.formatMessage({
        defaultMessage: 'Failed to connect to webinar room.',
        id: 'xQOiK4',
      }),

      { reload: 'ask' }
    )
    return undefined
  }

  twilioRoom?.removeAllListeners()
  twilioRoom = newTwilioRoom

  const { localParticipant, participants } = twilioRoom

  const isSecondCam = getIsClientSecondCam()

  // This guards against the same producer or speaker connecting more
  // than once. This should not be run for second cam.
  if (!isSecondCam && isParticipantDuplicate(localParticipant, participants)) {
    onDisconnect({ abort: true })
    setFatalError(
      intl.formatMessage({
        defaultMessage:
          'Another presenter took over your session. Maybe you joined twice?',
        id: 'BIdZKl',
      })
    )
    return undefined
  }

  initParticipant(twilioRoom.localParticipant, true)
  twilioRoom.participants.forEach((p) => {
    initParticipant(p, false)
  })

  twilioRoom.on('participantConnected', (p) => initParticipant(p, false))
  twilioRoom.on('participantDisconnected', onParticipantDisconnected)

  updateStatus('connected')

  twilioRoom.once('disconnected', (room, error) => {
    logMessage('Twilio disconnected', {
      level: 'log',
      extra: { error, state: room.state },
    })
    onDisconnect()
  })

  twilioRoom.on('reconnecting', (error) => {
    const message =
      error.code === 53001
        ? 'Reconnecting twilio signaling connection.'
        : error.code === 53405
          ? 'Reconnecting twilio media connection.'
          : 'Reconnecting twilio.'

    logMessage(message, {
      level: 'warning',
      extra: { error, state: twilioRoom?.state },
    })
  })

  twilioRoom.on('reconnected', () => {
    logMessage('Twilio reconnected', {
      level: 'log',
      extra: { state: twilioRoom?.state },
    })
  })

  window.addEventListener('pagehide', () => onDisconnect())

  return {
    publishTrack: async (mediaTrack: MediaStreamTrack, key: TrackKey) => {
      await twilioRoom?.localParticipant.publishTrack(mediaTrack, {
        name: `${key}.${Date.now()}`,
      })
    },

    unpublishTrack: (mediaTrack: MediaStreamTrack, key: TrackKey) => {
      if (!twilioRoom) return
      twilioRoom.localParticipant.unpublishTrack(mediaTrack)
      removeTrack(
        extractIdFromIdentity(twilioRoom.localParticipant.identity),
        key
      )
    },

    setTrackState: (key: LocalTrackKey, enabled: boolean) => {
      if (!twilioRoom) return
      let tracks
      switch (key) {
        case 'camera':
        case 'screen':
          tracks = twilioRoom.localParticipant.videoTracks
          break
        case 'cameraAudio':
        case 'screenAudio':
          tracks = twilioRoom.localParticipant.audioTracks
          break
        default:
          logMessage('Attempted to set the state of track with invalid key', {
            level: 'warning',
            extra: { key },
          })
          return
      }

      tracks.forEach((publication) => {
        if (getNormalizedTrackName(publication.trackName) === key) {
          if (enabled) {
            publication.track.enable()
          } else {
            publication.track.disable()
          }
        }
      })
    },
    setVideoTrackQuality: (
      remoteParticipantId: number,
      key: VideoTrackKey,
      priority: TrackPriority
    ) => {
      if (!twilioRoom) return

      const participant = Array.from(twilioRoom.participants.values()).find(
        (remoteParticipant) =>
          extractIdFromIdentity(remoteParticipant.identity) ===
          remoteParticipantId
      )

      participant?.videoTracks.forEach((publication) => {
        if (getNormalizedTrackName(publication.trackName) === key) {
          publication.track?.setContentPreferences?.({
            renderDimensions: RENDER_DIMENSIONS[priority],
          })
        }
      })
    },
    getProviderInfo: () => {
      if (!twilioRoom) return null
      return {
        providerName: 'twilio',
        identity: twilioRoom.localParticipant.identity,
        sid: twilioRoom.localParticipant.sid,
        state: twilioRoom.state,
        media_region: twilioRoom.mediaRegion,
        signaling_region: twilioRoom.localParticipant.signalingRegion,
        participant_state: twilioRoom.localParticipant.state,
        customExtras: {},
      }
    },
    getConnectionStats: async () => {
      const twilioLocalParticipant = twilioRoom?.localParticipant
      if (!twilioLocalParticipant) return null

      const videoNetworkQualityStats =
        twilioLocalParticipant.networkQualityStats?.video ?? null
      const audioNetworkQualityStats =
        twilioLocalParticipant.networkQualityStats?.audio ?? null

      const trackStats = await getStats()

      const formatStats = (stats: NetworkQualityMediaStats | null) => {
        if (!stats) return null
        return {
          send: {
            qualityLevel: stats.send,
            bandwidthAvailable: stats.sendStats?.bandwidth?.available ?? null,
            bandwidthActual: stats.sendStats?.bandwidth?.actual ?? null,
            bandwidthFractionLost:
              stats.sendStats?.fractionLost?.fractionLost ?? null,
          },
          receive: {
            qualityLevel: stats.recv,
            bandwidthAvailable: stats.recvStats?.bandwidth?.available ?? null,
            bandwidthActual: stats.recvStats?.bandwidth?.actual ?? null,
            bandwidthFractionLost:
              stats.recvStats?.fractionLost?.fractionLost ?? null,
          },
        }
      }

      const { connectionNetworkType, connectionNetworkProtocol } =
        await getTransportNetworkDetails()

      return {
        qualityLevel: twilioLocalParticipant.networkQualityLevel,
        qualityStats: twilioLocalParticipant.networkQualityStats,
        type: connectionNetworkType,
        protocol: connectionNetworkProtocol,
        trackStats,
        audioStats: formatStats(audioNetworkQualityStats),
        videoStats: formatStats(videoNetworkQualityStats),
      } as const
    },
  }
}

const getRtcReports = (room: Room) => async (twilioStat: StatsReport) => {
  // Unsafe code using an undocumented API, so wrapping in a try/catch. In case
  // of an exception, returning a sane value.
  try {
    // @ts-expect-error — _signaling is not typed
    // eslint-disable-next-line no-underscore-dangle
    return room._signaling._peerConnectionManager._peerConnections
      .get(twilioStat.peerConnectionId)
      ._peerConnection.getStats(null)
  } catch {
    return []
  }
}

const getTransportNetworkDetails = async () => {
  let connectionNetworkType = 'unknown'
  let connectionNetworkProtocol = 'unknown'

  if (!twilioRoom) return { connectionNetworkType, connectionNetworkProtocol }

  const twilioStats = await twilioRoom.getStats()
  const reports = await Promise.all(twilioStats.map(getRtcReports(twilioRoom)))
  if (reports.length > 0) {
    const firstReportSet = reports[0]

    let guessedTransportID = ''
    firstReportSet.forEach((report: any) => {
      if (report.type === 'outbound-rtp' && !guessedTransportID.length) {
        guessedTransportID = report.transportId || ''
      }
    })
    firstReportSet.forEach((report: any) => {
      if (
        report.type === 'local-candidate' &&
        guessedTransportID === report.transportId
      ) {
        connectionNetworkType = report.networkType
        connectionNetworkProtocol = report.protocol
      }
    })
  }
  return { connectionNetworkType, connectionNetworkProtocol }
}

const getOutboundMatrix = (reportStats: any) => {
  return Array.from(reportStats.values()).filter(
    (stat: any) => stat.type === 'outbound-rtp'
  )
}

const findStat = (matrixes: any, ssrc: any) =>
  matrixes.flat().find((matrix: any) => String(matrix.ssrc) === ssrc)

const appendWebrtcStats = (matrix: any) => (y: any) => ({
  ...y,
  ...{
    webrtcStats: findStat(matrix, y.ssrc),
  },
})

const getStats = async () => {
  if (!twilioRoom) return null
  const twilioStats = await twilioRoom.getStats()
  const reports = await Promise.all(twilioStats.map(getRtcReports(twilioRoom)))
  const outboundMatrix = reports.map(getOutboundMatrix)

  return twilioStats.reduce((acc: any, stat) => {
    if (stat.localVideoTrackStats) {
      acc.localVideoTrackStats = stat.localVideoTrackStats.map(
        appendWebrtcStats(outboundMatrix)
      )
    }

    if (stat.localAudioTrackStats) {
      acc.localAudioTrackStats = stat.localAudioTrackStats.map(
        appendWebrtcStats(outboundMatrix)
      )
    }

    return { ...stat, ...acc }
  }, {})
}

const onDisconnect = ({ abort = false }: { abort?: boolean } = {}) => {
  twilioRoom?.removeAllListeners()

  if (twilioRoom?.state !== 'disconnected') {
    try {
      twilioRoom?.disconnect()
    } catch (e) {
      // nothing
    }
  }

  twilioRoom = undefined
  disconnect({ abort })
}

// =================================

const initParticipant = (
  participant: RemoteParticipant | LocalParticipant,
  isLocal: boolean
) => {
  if (!twilioRoom) return

  const { id, role, joinedAt, name } = extractInfoFromIdentity(
    participant.identity
  )

  addParticipant({
    id,
    name,
    role,
    isLocal,
    joinedAt,
    tracks: {
      camera: null,
      cameraAudio: null,
      screen: null,
      screenAudio: null,
      mobile: null,
    },
  })

  updateParticipantQuality(
    id,
    translateNetworkQualityLevel(participant.networkQualityLevel)
  )

  if (!isLocal) {
    participant.tracks.forEach((track) => {
      if (track.track) onTrackSubscribed(id, track.track)
    })
  }

  participant.on('networkQualityLevelChanged', (level) => {
    updateParticipantQuality(id, translateNetworkQualityLevel(level))
  })

  if (isLocal) {
    const localParticipant = participant as LocalParticipant
    localParticipant.on('trackPublished', (publication) => {
      if (publication.track) onTrackSubscribed(id, publication.track)
    })
  } else {
    const remoteParticipant = participant as RemoteParticipant
    remoteParticipant.on('trackSubscribed', (track, _publication) => {
      onTrackSubscribed(id, track)
    })

    remoteParticipant.on('trackUnsubscribed', (track, _publication) => {
      onTrackUnsubscribed(id, track)
    })
  }
}

// ===========

const isParticipantSecondCam = (participant: Video.Participant) =>
  Array.from(participant.videoTracks?.values() ?? []).some((track) =>
    track.trackName.includes('mobile')
  )

const isParticipantDuplicate = (
  localParticipant: Participant,
  participants: Map<string, Participant>
) => {
  const participantId = extractIdFromIdentity(localParticipant.identity)
  return Array.from(participants.values() ?? []).some(
    (participant) =>
      extractIdFromIdentity(participant.identity) === participantId &&
      !isParticipantSecondCam(participant)
  )
}

// ===========

const onParticipantDisconnected = (participant: Participant) => {
  const participantId = extractIdFromIdentity(participant.identity)
  if (!twilioRoom) return

  const allParticipants = [
    twilioRoom.localParticipant,
    ...twilioRoom.participants.values(),
  ]

  if (
    !allParticipants.some(
      (p) => extractIdFromIdentity(p.identity) === participantId
    )
  ) {
    removeParticipant(participantId)
  }
}

const onTrackSubscribed = (id: number, track: RemoteTrack | LocalTrack) => {
  if (track.kind === 'data') {
    console.warn(
      `Track of type \`data\` found for participant with id ${id}. Ignoring`
    )
    return
  }

  const trackKey = getNormalizedTrackName(track.name)

  if (trackKey === 'unknown') {
    logMessage('Ignoring track of unknown type found for participant', {
      level: 'warning',
      extra: {
        participantId: id,
      },
    })
    return
  }

  const { isEnabled, mediaStreamTrack: stream } = track
  const storeTrack = addTrack(id, trackKey, {
    mediaStream: stream,
    isEnabled,
  })

  if (storeTrack) {
    track.on('enabled', () => updateTrackState(storeTrack, true))
    track.on('disabled', () => updateTrackState(storeTrack, false))
  }
}

const onTrackUnsubscribed = (id: number, track: RemoteTrack | LocalTrack) => {
  if (track.kind === 'data') {
    logMessage('Detected data track in twilio participant', {
      level: 'debug',
      extra: { id, track },
    })
    return
  }

  const trackKey = getNormalizedTrackName(track.name)
  if (trackKey === 'unknown') {
    logMessage('Detected unknown track in twilio participant', {
      level: 'debug',
      extra: { id, track },
    })
    return
  }

  removeTrack(id, trackKey)
}

const getTwilioRoom = () => twilioRoom

export {
  connect as connectToTwilio,
  onDisconnect as disconnectFromTwilio,
  getTwilioRoom,
}
